/** Org display profile — name + ISO 3166 country code.
 *
 * KYB-tied fields (legal_name, vat_id, etc.) live behind the KYB card
 * so the registry-validation pipeline owns those edits. This card is
 * just the human-visible chrome.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CountrySelector } from "@/components/CountrySelector";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError, isMfaRequired } from "@/lib/apiErrors";
import type { Organization } from "@/lib/types";


export function OrgProfileCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });

  const [name, setName] = useState("");
  const [country, setCountry] = useState("");

  useEffect(() => {
    if (org.data) {
      setName(org.data.name);
      setCountry(org.data.country ?? "");
    }
  }, [org.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch<Organization>(ApiPaths.orgProfile, {
        name: name.trim(),
        country: country.trim().toUpperCase(),
      });
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-me"], next);
    },
  });

  if (org.isLoading) return null;
  if (!org.data) return null;

  const dirty =
    name.trim() !== org.data.name ||
    country.trim().toUpperCase() !== (org.data.country ?? "");

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.orgProfile.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">
          {t("admin.orgProfile.cardHint")}
        </p>
      </header>
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("admin.orgProfile.nameLabel")}</span>
          <input
            type="text"
            className="input w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
          />
        </label>
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("admin.orgProfile.countryLabel")}</span>
          <CountrySelector
            value={country}
            onChange={(code) => setCountry(code)}
            allowEmpty
            aria-label={t("admin.orgProfile.countryLabel")}
          />
        </label>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => save.mutate()}
          disabled={!dirty || !name.trim() || save.isPending}
        >
          {save.isPending
            ? t("admin.orgProfile.saving")
            : t("admin.orgProfile.saveButton")}
        </button>
        {save.isSuccess && !dirty && (
          <span className="text-xs text-cvp-ok">
            {t("admin.orgProfile.savedAck")}
          </span>
        )}
      </div>
      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {isMfaRequired(save.error)
            ? t("admin.mfaRequiredHint")
            : describeApiError(
                save.error,
                t("admin.orgProfile.saveErrorGeneric"),
              )}
        </p>
      )}
    </section>
  );
}
